/**
 * PhotoUploadButton Component
 * 
 * Floating action button for uploading photos.
 */

import React, { useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  Pressable,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";

interface PhotoUploadButtonProps {
  onPickPhotos: () => void;
  onTakePhoto: () => void;
}

/**
 * FAB with upload options
 */
export const PhotoUploadButton: React.FC<PhotoUploadButtonProps> = ({
  onPickPhotos,
  onTakePhoto,
}) => {
  const colors = useThemeColors();
  const insets = useSafeAreaInsets();
  const [showOptions, setShowOptions] = useState(false);

  const handlePickPhotos = () => {
    setShowOptions(false);
    onPickPhotos();
  };

  const handleTakePhoto = () => {
    setShowOptions(false);
    onTakePhoto();
  };

  return (
    <>
      {/* FAB */}
      <TouchableOpacity
        onPress={() => setShowOptions(true)}
        className="absolute items-center justify-center rounded-full shadow-lg"
        style={{
          right: 16,
          bottom: Math.max(insets.bottom, 16) + 16,
          width: 56,
          height: 56,
          backgroundColor: colors.primary,
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.3,
          shadowRadius: 8,
          elevation: 8,
        }}
        accessibilityRole="button"
        accessibilityLabel="Foto uploaden"
      >
        <Ionicons name="add" size={28} color="#FFFFFF" />
      </TouchableOpacity>

      {/* Options modal */}
      <Modal
        visible={showOptions}
        transparent
        animationType="fade"
        onRequestClose={() => setShowOptions(false)}
      >
        <Pressable
          className="flex-1 justify-end"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onPress={() => setShowOptions(false)}
        >
          <View
            className="rounded-t-3xl"
            style={{
              backgroundColor: colors.surface,
              paddingBottom: Math.max(insets.bottom, 16),
            }}
          >
            {/* Header */}
            <View className="items-center py-4">
              <View
                className="w-10 h-1 rounded-full mb-4"
                style={{ backgroundColor: colors.border }}
              />
              <Text
                className="text-lg font-semibold"
                style={{ color: colors.text }}
              >
                Foto toevoegen
              </Text>
            </View>

            {/* Options */}
            <View className="px-4">
              {/* Take photo */}
              <TouchableOpacity
                onPress={handleTakePhoto}
                className="flex-row items-center py-4"
                style={{
                  borderBottomWidth: 1,
                  borderBottomColor: colors.border,
                }}
                accessibilityRole="button"
                accessibilityLabel="Foto maken"
              >
                <View
                  className="w-12 h-12 rounded-full items-center justify-center mr-4"
                  style={{ backgroundColor: `${colors.primary}15` }}
                >
                  <Ionicons name="camera" size={24} color={colors.primary} />
                </View>
                <View className="flex-1">
                  <Text
                    className="font-medium text-base"
                    style={{ color: colors.text }}
                  >
                    Foto maken
                  </Text>
                  <Text
                    className="text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    Maak een nieuwe foto met je camera
                  </Text>
                </View>
                <Ionicons
                  name="chevron-forward"
                  size={20}
                  color={colors.textTertiary}
                />
              </TouchableOpacity>

              {/* Pick from gallery */}
              <TouchableOpacity
                onPress={handlePickPhotos}
                className="flex-row items-center py-4"
                accessibilityRole="button"
                accessibilityLabel="Foto's kiezen"
              >
                <View
                  className="w-12 h-12 rounded-full items-center justify-center mr-4"
                  style={{ backgroundColor: `${colors.primary}15` }}
                >
                  <Ionicons name="images" size={24} color={colors.primary} />
                </View>
                <View className="flex-1">
                  <Text
                    className="font-medium text-base"
                    style={{ color: colors.text }}
                  >
                    Kies uit galerij
                  </Text>
                  <Text
                    className="text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    Selecteer één of meerdere foto's
                  </Text>
                </View>
                <Ionicons
                  name="chevron-forward"
                  size={20}
                  color={colors.textTertiary}
                />
              </TouchableOpacity>
            </View>

            {/* Cancel button */}
            <View className="px-4 pt-4">
              <TouchableOpacity
                onPress={() => setShowOptions(false)}
                className="py-3 rounded-xl items-center"
                style={{ backgroundColor: colors.surfaceSecondary }}
                accessibilityRole="button"
                accessibilityLabel="Annuleren"
              >
                <Text
                  className="font-medium"
                  style={{ color: colors.textSecondary }}
                >
                  Annuleren
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </Pressable>
      </Modal>
    </>
  );
};

export default PhotoUploadButton;

